"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getStackItems = exports.getLabelOrDataLabelForPoints = exports.handleColumnLabelsOutsideChart = exports.autohideColumnLabels = exports.getStackTotalGroups = exports.getStackLabelPointsForDualAxis = exports.areLabelsOverlappingColumns = exports.areNeighborsOverlapping = exports.isOverlappingWidth = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var map_1 = tslib_1.__importDefault(require("lodash/map"));
var zip_1 = tslib_1.__importDefault(require("lodash/zip"));
var values_1 = tslib_1.__importDefault(require("lodash/values"));
var flatten_1 = tslib_1.__importDefault(require("lodash/flatten"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var helpers_1 = require("../../../chartTypes/_chartCreators/helpers");
var dataLabelsHelpers_1 = require("../../../chartTypes/_chartCreators/dataLabelsHelpers");
var sdk_ui_1 = require("@gooddata/sdk-ui");
/*
 * Code in this file accesses Highchart properties that are not included in
 * the official typings:
 *
 * -  Axis.stacking.stacks
 * -  Axis.stacking.stackTotalGroup
 * -  Series.dataLabelsGroup
 * -  Point.label
 * -  Point.dataLabel
 * -  Point.element
 * -  Point.graphic
 * -  Point.shapeArgs
 *
 * For some time, we included the 'extra' stuff in our own types (IAxisConfig). This type was in return
 * used in IChartConfig. The chart config should not be riddled with highchart internals - so that went away
 * and code here started to use Highchart types instead.
 *
 * By using the public Highchart types, the use of undocumented fields became more apparent. Instead of creating
 * types for these internals that we misuse (?), I have opted to casting as UnsafeInternals and accessing
 */
var toggleNonStackedChartLabels = function (visiblePoints, axisRangeForAxes, shouldCheckShapeIntersection) {
    if (shouldCheckShapeIntersection === void 0) { shouldCheckShapeIntersection = false; }
    var foundIntersection = helpers_1.toNeighbors(
    // some data labels may not be rendered (too many points)
    visiblePoints.filter(function (point) {
        return dataLabelsHelpers_1.hasDataLabel(point) && dataLabelsHelpers_1.hasShape(point);
    })).some(function (pointPair) {
        var _a = pointPair || [], firstPoint = _a[0], nextPoint = _a[1];
        var firstDataLabelAttr = dataLabelsHelpers_1.getDataLabelAttributes(firstPoint);
        var nextDataLabelAttr = dataLabelsHelpers_1.getDataLabelAttributes(nextPoint);
        if (shouldCheckShapeIntersection) {
            var firstShapeAttr = helpers_1.getShapeAttributes(firstPoint);
            var nextShapeAttr = helpers_1.getShapeAttributes(nextPoint);
            return (helpers_1.isIntersecting(firstDataLabelAttr, nextDataLabelAttr) ||
                helpers_1.isIntersecting(firstDataLabelAttr, nextShapeAttr) ||
                helpers_1.isIntersecting(firstShapeAttr, nextDataLabelAttr));
        }
        return helpers_1.isIntersecting(firstDataLabelAttr, nextDataLabelAttr);
    });
    if (foundIntersection) {
        dataLabelsHelpers_1.hideDataLabels(visiblePoints);
    }
    else {
        visiblePoints.forEach(function (point) { return dataLabelsHelpers_1.showDataLabelInAxisRange(point, point.y, axisRangeForAxes); });
    }
};
var toggleStackedChartLabels = function (visiblePoints, axisRangeForAxes) {
    var toggleLabel = function (point) {
        var dataLabel = point.dataLabel, shapeArgs = point.shapeArgs, chart = point.series.chart;
        if (dataLabel && shapeArgs) {
            var labelHeight = dataLabel.height + (2 * dataLabel.padding || 0);
            var shapeHeight = dataLabelsHelpers_1.getShapeVisiblePart(shapeArgs, chart, shapeArgs.height);
            var isOverlappingHeight = labelHeight > shapeHeight;
            return isOverlappingHeight
                ? dataLabelsHelpers_1.hideDataLabel(point)
                : // fix for HCH bug for negative stack labels
                    dataLabelsHelpers_1.showStackLabelInAxisRange(point, axisRangeForAxes);
        }
        return null;
    };
    if (isOverlappingWidth(visiblePoints)) {
        dataLabelsHelpers_1.hideDataLabels(visiblePoints);
    }
    else {
        visiblePoints.forEach(toggleLabel);
    }
};
function isOverlappingWidth(visiblePoints) {
    return visiblePoints.filter(dataLabelsHelpers_1.hasDataLabel).some(function (point) {
        var dataLabel = point.dataLabel, shapeArgs = point.shapeArgs;
        if (dataLabel && shapeArgs) {
            var labelWidth = dataLabel.width + 2 * dataLabel.padding;
            return labelWidth > shapeArgs.width;
        }
        return false;
    });
}
exports.isOverlappingWidth = isOverlappingWidth;
function areNeighborsOverlapping(neighbors) {
    return neighbors.some(function (labelsPair) {
        var _a = labelsPair || [], firstLabel = _a[0], nextLabel = _a[1];
        if (!isEmpty_1.default(firstLabel) && !isEmpty_1.default(nextLabel)) {
            var firstClientRect = firstLabel.element.getBoundingClientRect();
            var nextClientRect = nextLabel.element.getBoundingClientRect();
            if (firstClientRect && nextClientRect) {
                var firstLabelRight = firstClientRect.right;
                var nextLabelLeft = nextClientRect.left;
                return firstLabelRight > nextLabelLeft;
            }
        }
        return false;
    });
}
exports.areNeighborsOverlapping = areNeighborsOverlapping;
// Check if Total label overlapping other columns
function areLabelsOverlappingColumns(labels, visiblePoints) {
    return labels.some(function (label) {
        if (isEmpty_1.default(label)) {
            return false;
        }
        var _a = label.element.getBoundingClientRect(), x = _a.x, y = _a.y, width = _a.width, height = _a.height;
        var labelAttr = {
            x: x,
            y: y,
            width: width,
            height: height,
        };
        return visiblePoints.some(function (point) {
            var _a, _b;
            var seriesType = (_b = (_a = point === null || point === void 0 ? void 0 : point.series) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.type;
            if (isEmpty_1.default(point) ||
                isEmpty_1.default(point.graphic) ||
                // supportedDualAxesChartTypes is including AREA and LINE
                // won't hide the stacked label if it overlaps with points of AREA and LINE
                seriesType === sdk_ui_1.VisualizationTypes.AREA ||
                seriesType === sdk_ui_1.VisualizationTypes.LINE) {
                return false;
            }
            var _c = point.graphic.element.getBoundingClientRect(), x = _c.x, y = _c.y, width = _c.width, height = _c.height;
            var pointAttr = {
                x: x,
                y: y,
                width: width,
                height: height,
            };
            return helpers_1.isIntersecting(pointAttr, labelAttr);
        });
    });
}
exports.areLabelsOverlappingColumns = areLabelsOverlappingColumns;
function findColumnKey(key) {
    return key.indexOf("column") === 0;
}
/**
 * Merge stack label points from axes to one
 * Primary axis:    [pointP1, pointP2, pointP3]
 * Secondary axis:  [pointS1, pointS2, pointS3]
 * @returns [pointP1, pointS1, pointP2, pointS2, pointP3, pointS3]
 */
function getStackLabelPointsForDualAxis(stacks) {
    return flatten_1.default(
    // 'column0' is primary axis and 'column1' is secondary axis
    zip_1.default.apply(void 0, stacks.map(function (item) {
        var columnKey = Object.keys(item).find(findColumnKey);
        return values_1.default(item[columnKey]);
    }))).filter(identity_1.default);
}
exports.getStackLabelPointsForDualAxis = getStackLabelPointsForDualAxis;
function getStackTotalGroups(yAxis) {
    return flatten_1.default(yAxis.map(function (axis) {
        var _a, _b;
        if (!isEmpty_1.default((_a = axis) === null || _a === void 0 ? void 0 : _a.stacking.stacks)) {
            return (_b = axis) === null || _b === void 0 ? void 0 : _b.stacking.stackTotalGroup;
        }
        return axis.series.map(function (serie) { return serie.dataLabelsGroup; });
    })).filter(identity_1.default);
}
exports.getStackTotalGroups = getStackTotalGroups;
function toggleStackedLabelsForDualAxis() {
    var yAxis = this.yAxis;
    var stackTotalGroups = getStackTotalGroups(yAxis);
    var stacks = getStackItems(yAxis);
    if (stacks && stackTotalGroups) {
        var points = getStackLabelPointsForDualAxis(stacks);
        var labels = getLabelOrDataLabelForPoints(points);
        var neighbors = helpers_1.toNeighbors(labels);
        var neighborsOverlapping = areNeighborsOverlapping(neighbors);
        var areOverlapping_1 = neighborsOverlapping || areLabelsOverlappingColumns(labels, helpers_1.getDataPointsOfVisibleSeries(this));
        stackTotalGroups.forEach(function (stackTotalGroup) {
            return setVisibilityByOpacity(stackTotalGroup, !areOverlapping_1);
        });
    }
}
function toggleStackedLabelsForSingleAxis() {
    var yAxis = this.yAxis;
    var firstYAxis = yAxis[0] || {};
    var stacks = firstYAxis === null || firstYAxis === void 0 ? void 0 : firstYAxis.stacking.stacks;
    var stackTotalGroup = firstYAxis === null || firstYAxis === void 0 ? void 0 : firstYAxis.stacking.stackTotalGroup;
    if (stacks && stackTotalGroup) {
        var columnKey = Object.keys(stacks).find(findColumnKey);
        // We need to use Lodash map, because we are iterating through an object
        var labels = map_1.default(stacks[columnKey], function (point) { return point.label; });
        var neighbors = helpers_1.toNeighbors(labels);
        var stackTotalGroupVisible = !areNeighborsOverlapping(neighbors);
        setVisibilityByOpacity(stackTotalGroup, stackTotalGroupVisible);
    }
}
function setVisibilityByOpacity(stackTotalGroup, visible) {
    stackTotalGroup.attr({ opacity: visible ? 1 : 0 });
}
function toggleStackedLabels() {
    var yAxis = this.yAxis;
    // CL-10676 - Return if yAxis is undefined
    if (!yAxis || yAxis.length === 0) {
        return;
    }
    if (yAxis.length === 2) {
        return toggleStackedLabelsForDualAxis.call(this);
    }
    return toggleStackedLabelsForSingleAxis.call(this);
}
exports.autohideColumnLabels = function (chart) {
    var isStackedChart = helpers_1.isStacked(chart);
    var hasLabelsStacked = dataLabelsHelpers_1.areLabelsStacked(chart);
    var visiblePoints = helpers_1.getDataPointsOfVisibleSeries(chart);
    var axisRangeForAxes = helpers_1.getAxisRangeForAxes(chart);
    // stack chart labels is displayed inside column
    if (isStackedChart) {
        toggleStackedChartLabels(visiblePoints.filter(dataLabelsHelpers_1.hasLabelInside), axisRangeForAxes);
    }
    else {
        toggleNonStackedChartLabels(visiblePoints, axisRangeForAxes, true);
    }
    // stack labels are total values displayed on top of columns
    if (hasLabelsStacked) {
        toggleStackedLabels.call(chart);
    }
};
exports.handleColumnLabelsOutsideChart = function (chart) {
    var visiblePoints = helpers_1.getDataPointsOfVisibleSeries(chart);
    var axisRangeForAxes = helpers_1.getAxisRangeForAxes(chart);
    visiblePoints.forEach(function (point) {
        if (!helpers_1.isStacked(chart)) {
            dataLabelsHelpers_1.showDataLabelInAxisRange(point, point.y, axisRangeForAxes);
        }
        else {
            // fix for HCH bug for negative stack labels
            dataLabelsHelpers_1.showStackLabelInAxisRange(point, axisRangeForAxes);
        }
    });
};
function getLabelOrDataLabelForPoints(points) {
    return points
        .map(function (point) {
        return point.label || point.dataLabel;
    })
        .filter(identity_1.default);
}
exports.getLabelOrDataLabelForPoints = getLabelOrDataLabelForPoints;
function getStackItems(yAxis) {
    return flatten_1.default(yAxis.map(function (axis) {
        var _a, _b;
        if (!isEmpty_1.default((_a = axis) === null || _a === void 0 ? void 0 : _a.stacking.stacks)) {
            return (_b = axis) === null || _b === void 0 ? void 0 : _b.stacking.stacks;
        }
        var series = axis.series;
        return series.map(function (serie) {
            return {
                column: tslib_1.__assign({}, serie.data),
            };
        });
    }));
}
exports.getStackItems = getStackItems;
//# sourceMappingURL=autohideColumnLabels.js.map